import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_EARLY_PAYMENT_DETAILS,
    ACCOUNT_EARLY_PAYMENT_REMITTANCE_DETAILS,
    ACCOUNT_INVOICE_TERMS_PAYMENT_DETAILS,
    atlas,
    EARLY_PAYMENT_DETAILS,
    EARLY_PAYMENT_REMITTANCE_DETAILS,
    INVOICE_TERMS_PAYMENT_DETAILS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { GetPaymentsSummaryRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/get-payments-summary';
import {
    AccountPaymentsListRequest,
    BuyerPaymentsListRequest,
    GetPaymentsSummaryService
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    GetBuyerCurrentBalanceExportRequest,
    GetCurrentBalanceExportService
} from '@lsq-frontend/remote-services/report-center';

import {
    GetBuyerTotalBalanceExportRequest,
    GetTotalBalanceExportService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-balance-summary/get-total-balance-export';
import { BuyerPaymentsListResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/list-payments';
import { AccountPaymentsListResponse } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/list-account-payments';
import {
    ListAccountPaymentsService,
    ListPaymentsService
} from '@lsq-frontend/remote-services/financing-graphql';
import {
    AccountPaymentsDetailExportRequest,
    GetAccountPaymentExportService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/get-account-payment-export';
import { BuyerPaymentsSummaryItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-payments-summary-item';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ActionSheet,
    ActionSheets,
    Color,
    DatePickerType,
    getDynamicRouterLink,
    ISelectOption,
    LsqBarDataPoint,
    Table,
    TableHelper
} from '@lsq-frontend/common-ui';
import {
    ActionSheetService,
    ClaimService,
    FeatureFlagService,
    getRouterLinkById,
    Icons,
    LsqCurrencyPipe,
    LsqDatePipe,
    ResponsiveService,
    TruncateTextPipe,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { format, subDays } from 'date-fns';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain-direct-buyer-account-shared.service';
import { parseYearMonthDayStringToDate } from '../../../../../helpers/date.helpers';
import { AccountPaymentsSummaryItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/account-payments-summary-item';
import { SupplyChainDirectAccountService } from '../supply-chain-direct-account.service';
import { combineLatest } from 'rxjs';
import { IdentityService } from '@lsq-frontend/fasttrack-shared/features/identity-service';
import {
    BuyerPaymentsDetailExportRequest,
    GetBuyerPaymentDetailsExportService
} from '@lsq-frontend/remote-services/report-center';

export enum BuyerPaymentsPayerFilterEnum {
    ALL_PAYERS,
    LSQ,
    BUYER,
    DEPOSIT_ACCOUNT
}

export enum BuyerPaymentsPayerFilterControlEnum {
    ALL_PAYERS,
    LSQ,
    BUYER
}

@Component({
    selector: 'supply-chain-direct-buyer-account-payments',
    templateUrl: './payments.component.html',
    styleUrls: ['./payments.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerAccountPaymentsComponent implements OnInit {
    @ViewChild('dateSelectTemplate', { static: true })
    public dateSelectTemplateRef: TemplateRef<any>;
    public isBuyerUser?: boolean;
    public dataPoints: LsqBarDataPoint[] = [];
    public readonly color = Color;
    public readonly icons = Icons;
    public readonly nullValue = Table.NullAmount;
    public readonly datePickerType = DatePickerType;
    public payerTypeDisplay = {};
    public isLoaded = false;
    public readonly today = new Date();
    public readonly thirtyDaysAgo = subDays(new Date(), 30);
    public dateSelectMobileActionSheet: ActionSheet;
    public payerFilterOptions: ISelectOption[] = [];
    public dataSource: LsqApiServiceListSource<
        BuyerPaymentsListResponse | AccountPaymentsListResponse,
        BuyerPaymentsListRequest | AccountPaymentsListRequest,
        BuyerPaymentsSummaryItem | AccountPaymentsSummaryItem
    >;
    public paymentsFiltersFormGroup: FormGroup;
    public isFinancingAccountPortalUser = false;
    public buyerPaymentsPayerFilterEnum = BuyerPaymentsPayerFilterEnum;
    public isBuyerAccountPaymentsPageOn$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerAccountPaymentsPage
    );
    public isPayLater$ = this._featureFlagService.isOn(FeatureFlags.PayLater);
    public isPayLater = false;

    public readonly paymentsHeader = this._i18n('Payment Date');
    public readonly referenceNumberHeader = this._i18n('Reference Number');
    public readonly payerHeader = this._i18n('Payer');
    public readonly recipientHeader = this._i18n('Recipient');
    public readonly typeHeader = this._i18n('Type');
    public readonly invoicesHeader = this._i18n('Invoices');
    public readonly paymentAmountHeader = this._i18n('Payment Amount');
    public readonly exportHeader = this._i18n('Export');

    public readonly openPaymentDetailsBound = this.openPaymentDetails.bind(this);
    public readonly getReportNameBound = this.getReportName.bind(this);
    public readonly createPaymentExportRequestBound = this.createPaymentExportRequest.bind(this);

    public readonly referenceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 27;
            }

            if (dimensions.width > 1100) {
                return 18;
            }
            return 12;
        })
    );

    public readonly buyerPaymentsPayerFilterTranslation: Record<
        string,
        BuyerPaymentsPayerFilterEnum[]
    > = {
        ALL_PAYERS: [],
        LSQ: [BuyerPaymentsPayerFilterEnum.LSQ],
        BUYER: [BuyerPaymentsPayerFilterEnum.DEPOSIT_ACCOUNT, BuyerPaymentsPayerFilterEnum.BUYER]
    };

    public readonly buyerPaymentsPaymentTypeDisplayEnum = {
        EARLY_PAYMENT: this._i18n('Early Payment'),
        EARLY_PAY_REMITTANCE: this._i18n('Early Pay Remittance'),
        INVOICE_TERM: this._i18n('Paid at Terms'),
        PAY_LATER: this._i18n('FlexTerm'),
        UNKNOWN_PAYMENT_TYPE: ''
    };

    public readonly buyerPaymentsRecipientDisplayEnum = {
        LSQ: this._i18n('LSQ'),
        SUPPLIERS: this._i18n('Supplier(s)'),
        'LSQ And SUPPLIERS': this._i18n('LSQ and Supplier(s)'),
        BUYER: this._i18n('Buyer'),
        UNKNOWN_RECIPIENT: ''
    };

    constructor(
        private _listPaymentsService: ListPaymentsService,
        private _getPaymentsSummaryService: GetPaymentsSummaryService,
        private _lsqCurrencyPipe: LsqCurrencyPipe,
        private _truncateTextPipe: TruncateTextPipe,
        private _userService: UserService,
        public responsiveService: ResponsiveService,
        public actionSheetService: ActionSheetService,
        public windowReferenceService: WindowReferenceService,
        private _i18n: I18n,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        public lsqDatePipe: LsqDatePipe,
        public getPaymentExportService: GetBuyerPaymentDetailsExportService,
        public getAccountPaymentExportService: GetAccountPaymentExportService,
        public getCurrentBalanceExportService: GetCurrentBalanceExportService,
        public getTotalBalanceExportService: GetTotalBalanceExportService,
        private _claimService: ClaimService,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _listAccountsPaymentsService: ListAccountPaymentsService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService,
        private _featureFlagService: FeatureFlagService,
        private _identityService: IdentityService
    ) {
        this.isPayLater$.subscribe((isPayLater) => {
            this.isPayLater = isPayLater;
        });
    }

    public async ngOnInit(): Promise<void> {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
        this.isBuyerUser = this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal);
        this._identityService.companyName$.subscribe((companyName) => {
            this.buyerPaymentsRecipientDisplayEnum.BUYER = companyName;

            this.payerTypeDisplay = {
                LSQ: this._i18n('LSQ'),
                DEPOSIT_ACCOUNT: this.isFinancingAccountPortalUser
                    ? this._i18n('All Business Units')
                    : companyName,
                BUYER: this.isFinancingAccountPortalUser
                    ? this._i18n('All Business Units')
                    : companyName,
                UNKNOWN_PAYER: ''
            };

            this.payerFilterOptions = [
                {
                    label: this._i18n('All Payers'),
                    value: BuyerPaymentsPayerFilterControlEnum.ALL_PAYERS
                },
                {
                    label: this._i18n('LSQ'),
                    value: BuyerPaymentsPayerFilterControlEnum.LSQ
                },
                {
                    label: this.isFinancingAccountPortalUser
                        ? this._i18n('All Business Units')
                        : this._truncateTextPipe.transform(companyName ?? '', 23),
                    value: BuyerPaymentsPayerFilterControlEnum.BUYER,
                    titleText: companyName?.length > 23 ? companyName : undefined
                }
            ];
        });

        const defaultPagination = {
            skip: 0,
            take: 25,
            sortColumn: 'paymentDate',
            sortDirection: SortDirectionFinancing.DESCENDING
        };
        const queryParamMap = this._activatedRoute.snapshot.queryParamMap;

        const pagination = TableHelper.getPaginationByQueryParams(
            this._activatedRoute.snapshot.queryParamMap
        );

        const startDate: string = queryParamMap.get(FastTrackRouteQueryParams.StartDate) || '';
        const endDate: string = queryParamMap.get(FastTrackRouteQueryParams.EndDate) || '';

        const searchText = queryParamMap.get(FastTrackRouteQueryParams.SearchText);

        const searchInvoiceText = queryParamMap.get(FastTrackRouteQueryParams.SearchInvoiceNumber);

        const payerFilter = queryParamMap.get(FastTrackRouteQueryParams.PayerFilter)
            ? queryParamMap.get(FastTrackRouteQueryParams.PayerFilter)
            : undefined;
        const payFilterValues = payerFilter
            ? this.buyerPaymentsPayerFilterTranslation[payerFilter]
            : undefined;

        if (this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
            this.dataSource = new LsqApiServiceListSource(
                this._listAccountsPaymentsService as unknown as ListPaymentsService,
                new AccountPaymentsListRequest({
                    pagination: {
                        ...defaultPagination,
                        ...pagination
                    } as any,
                    searchInvoiceNumber: searchInvoiceText || '',
                    searchValue: searchText || '',
                    dateRangeStart: startDate || format(this.thirtyDaysAgo, 'YYYY-MM-DD'),
                    dateRangeEnd: endDate || format(this.today, 'YYYY-MM-DD'),
                    accountPaymentsListPayerEnums: payFilterValues
                        ? (payFilterValues.map(
                              (filter) => BuyerPaymentsPayerFilterEnum[filter]
                          ) as any[])
                        : [],
                    buyerKeys:
                        this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                            ?.value ?? []
                })
            );
        } else {
            this.dataSource = new LsqApiServiceListSource(
                this._listPaymentsService,
                new BuyerPaymentsListRequest({
                    pagination: {
                        ...defaultPagination,
                        ...pagination
                    } as any,
                    searchInvoiceNumber: searchInvoiceText || '',
                    searchValue: searchText || '',
                    dateRangeStart: startDate || format(this.thirtyDaysAgo, 'YYYY-MM-DD'),
                    dateRangeEnd: endDate || format(this.today, 'YYYY-MM-DD'),
                    buyerPaymentListPayerEnums: payFilterValues
                        ? (payFilterValues.map(
                              (filter) => BuyerPaymentsPayerFilterEnum[filter]
                          ) as any[])
                        : []
                })
            );
        }

        this.paymentsFiltersFormGroup = new FormGroup({
            dateFilter: new FormControl({
                start: startDate ? parseYearMonthDayStringToDate(startDate) : this.thirtyDaysAgo,
                end: endDate ? parseYearMonthDayStringToDate(endDate) : this.today
            }),
            payerFilter: new FormControl(
                payerFilter
                    ? BuyerPaymentsPayerFilterControlEnum[
                          payerFilter as keyof typeof BuyerPaymentsPayerFilterControlEnum
                      ]
                    : BuyerPaymentsPayerFilterControlEnum.ALL_PAYERS
            ),
            search: new FormControl(searchText || ''),
            searchInvoiceNumber: new FormControl(searchInvoiceText || '')
        });

        this.dateSelectMobileActionSheet = new ActionSheet({
            id: ActionSheets.DatePickerMobile,
            templateRef: this.dateSelectTemplateRef
        });

        combineLatest([
            this.paymentsFiltersFormGroup.valueChanges.pipe(
                distinctUntilChanged(),
                startWith(undefined)
            ),
            this._supplyChainDirectAccountService.buyerKeysFormGroup.valueChanges.pipe(
                distinctUntilChanged(),
                startWith(undefined)
            )
        ]).subscribe(([filters, accountFilter]) => {
            const currentFilters = filters || this.paymentsFiltersFormGroup.value;
            const oldRequest = this.dataSource.request;
            const paymentFilterEnum =
                this.buyerPaymentsPayerFilterTranslation[
                    BuyerPaymentsPayerFilterControlEnum[currentFilters.payerFilter]
                ];

            if (this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
                this.dataSource.dispatchRequest(
                    new AccountPaymentsListRequest({
                        ...oldRequest,
                        searchInvoiceNumber: currentFilters?.searchInvoiceNumber || '',
                        searchValue: currentFilters?.search || '',
                        accountPaymentsListPayerEnums: paymentFilterEnum.map(
                            (filter) => BuyerPaymentsPayerFilterEnum[filter]
                        ) as any[],
                        dateRangeStart: format(currentFilters?.dateFilter.start, 'YYYY-MM-DD'),
                        dateRangeEnd: format(currentFilters?.dateFilter.end, 'YYYY-MM-DD'),
                        pagination: {
                            ...oldRequest.pagination,
                            skip: 0
                        },
                        buyerKeys:
                            this._supplyChainDirectAccountService.buyerKeysFormGroup.get(
                                'buyerKeys'
                            )?.value ?? []
                    })
                );
            } else {
                this.dataSource.dispatchRequest(
                    new BuyerPaymentsListRequest({
                        ...oldRequest,
                        searchInvoiceNumber: currentFilters?.searchInvoiceNumber || '',
                        searchValue: currentFilters?.search || '',
                        buyerPaymentListPayerEnums: paymentFilterEnum.map(
                            (filter) => BuyerPaymentsPayerFilterEnum[filter]
                        ) as any[],
                        dateRangeStart: format(currentFilters?.dateFilter.start, 'YYYY-MM-DD'),
                        dateRangeEnd: format(currentFilters?.dateFilter.end, 'YYYY-MM-DD'),
                        pagination: {
                            ...oldRequest.pagination,
                            skip: 0
                        }
                    })
                );
            }
        });

        this.dataSource.data$.subscribe(() => {
            this.isLoaded = true;
            this.setFilterQueryParams();
        });

        const response = await this._getPaymentsSummaryService.executeOnce(
            new GetPaymentsSummaryRequest({
                buyerKeys: this.isFinancingAccountPortalUser
                    ? this._supplyChainDirectAccountService.buyerKeysFormGroup.get('buyerKeys')
                          ?.value ?? []
                    : [this._userService.getAlternateCompanyKey()]
            })
        );

        this.dataPoints = [
            {
                label: this._i18n('Current Balance Due'),
                data: this._lsqCurrencyPipe.transform(
                    response.paymentsSummary?.currentBalance || 0
                ),
                exportInformation:
                    this.isFinancingAccountPortalUser ||
                    response.paymentsSummary?.currentBalance == 0
                        ? undefined
                        : {
                              reportName: `Current_Balance_Due_${this.lsqDatePipe
                                  .transform(this.today, 'shortDate')
                                  .replace(/\//g, '-')}`,
                              service: this.getCurrentBalanceExportService,
                              request: new GetBuyerCurrentBalanceExportRequest({
                                  actualBuyerDueDateRange: {
                                      endDate: this.today?.toISOString().split('T')[0]
                                  },
                                  pagination: {
                                      skip: 0,
                                      take: 25,
                                      sortColumn: 'invoiceDueDate',
                                      sortDirection: SortDirectionFinancing.DESCENDING
                                  }
                              })
                          }
            },
            ...(this.isPayLater
                ? []
                : [
                      {
                          label: this._i18n('Late Fees'),
                          data: this._lsqCurrencyPipe.transform(
                              response.paymentsSummary?.lateFees || 0
                          )
                      }
                  ]),
            {
                label: this._i18n('Remaining Balance'),
                data: this._lsqCurrencyPipe.transform(
                    response.paymentsSummary?.remainingBalance || 0
                )
            },
            {
                label: this._i18n('Your Total Balance'),
                data: this._lsqCurrencyPipe.transform(response.paymentsSummary?.totalBalance || 0),
                exportInformation:
                    this.isFinancingAccountPortalUser || this.isPayLater
                        ? undefined
                        : {
                              reportName: `Total_Balance_${this.lsqDatePipe
                                  .transform(this.today, 'shortDate')
                                  .replace(/\//g, '-')}`,
                              service: this.getTotalBalanceExportService,
                              request: new GetBuyerTotalBalanceExportRequest()
                          }
            }
        ];
    }

    public toggleDateSelectMobileDrawer(): void {
        this.actionSheetService.showActionSheet(this.dateSelectMobileActionSheet);
    }

    public async openPaymentDetails(payment: BuyerPaymentsSummaryItem): Promise<void> {
        // Set the filters right before we navigate to make sure the url is correct.
        // This protects against cases like clicking the navigation item for this page.
        await this.setFilterQueryParams();

        let paymentType = '';

        if (this.isBuyerUser) {
            if (payment.paymentType === 'EARLY_PAYMENT') {
                paymentType = EARLY_PAYMENT_DETAILS;
            } else if (payment.paymentType === 'EARLY_PAY_REMITTANCE') {
                paymentType = EARLY_PAYMENT_REMITTANCE_DETAILS;
            } else if (payment.paymentType === 'INVOICE_TERM') {
                paymentType = INVOICE_TERMS_PAYMENT_DETAILS;
            } else if ((payment.paymentType as string) === 'PAY_LATER_PAYMENT') {
                // TODO: Implement this for real
                paymentType = INVOICE_TERMS_PAYMENT_DETAILS;
            } else {
                // TODO: When the payment type is empty in BUYER_TO_FUNDER
                paymentType = INVOICE_TERMS_PAYMENT_DETAILS;
            }
        } else {
            if (payment.paymentType === 'EARLY_PAYMENT') {
                paymentType = ACCOUNT_EARLY_PAYMENT_DETAILS;
            } else if (payment.paymentType === 'EARLY_PAY_REMITTANCE') {
                paymentType = ACCOUNT_EARLY_PAYMENT_REMITTANCE_DETAILS;
            } else if (payment.paymentType === 'INVOICE_TERM') {
                paymentType = ACCOUNT_INVOICE_TERMS_PAYMENT_DETAILS;
            } else if ((payment.paymentType as string) === 'PAY_LATER_PAYMENT') {
                // TODO: Implement this for real
                paymentType = INVOICE_TERMS_PAYMENT_DETAILS;
            }
        }

        if (paymentType) {
            await this._router.navigateByUrl(
                this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, paymentType, {
                            noStandaloneLeadingSlash: true
                        }),
                        { earlyPaymentKey: payment.batchKey }
                    ),
                    {}
                )
            );
        }
    }

    public async setFilterQueryParams(): Promise<void> {
        const queryParams = {
            ...TableHelper.getQueryParamsByListSource(this.dataSource),
            [FastTrackRouteQueryParams.SearchText]:
                this.paymentsFiltersFormGroup.controls.search.value || undefined,
            [FastTrackRouteQueryParams.SearchInvoiceNumber]:
                this.paymentsFiltersFormGroup.controls.searchInvoiceNumber.value || undefined,
            [FastTrackRouteQueryParams.StartDate]: this.paymentsFiltersFormGroup.controls.dateFilter
                .value?.start
                ? format(
                      this.paymentsFiltersFormGroup.controls.dateFilter.value?.start,
                      'YYYY-MM-DD'
                  )
                : undefined,
            [FastTrackRouteQueryParams.EndDate]: this.paymentsFiltersFormGroup.controls.dateFilter
                .value?.end
                ? format(this.paymentsFiltersFormGroup.controls.dateFilter.value?.end, 'YYYY-MM-DD')
                : undefined,

            [FastTrackRouteQueryParams.PayerFilter]: this.paymentsFiltersFormGroup.controls
                .payerFilter?.value
                ? BuyerPaymentsPayerFilterControlEnum[
                      this.paymentsFiltersFormGroup.controls.payerFilter.value
                  ]
                : undefined
        };

        await this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    public createPaymentExportRequest(row: BuyerPaymentsSummaryItem) {
        if (this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
            return new AccountPaymentsDetailExportRequest({
                batchKey: row.batchKey,
                accountPaymentsListPaymentTypeEnum: row.paymentType,
                pagination: {
                    skip: 0,
                    take: 25,
                    sortColumn: 'invoiceDate',
                    sortDirection: SortDirectionFinancing.DESCENDING
                }
            });
        }
        return new BuyerPaymentsDetailExportRequest({
            batchKey: row.batchKey,
            buyerPaymentsListPaymentTypeEnum: row.paymentType,
            pagination: {
                skip: 0,
                take: 25,
                sortColumn: 'invoiceDate',
                sortDirection: SortDirectionFinancing.DESCENDING
            }
        });
    }

    public getReportName(row: BuyerPaymentsSummaryItem): string {
        const paymentDate = parseYearMonthDayStringToDate(row.paymentDate!);
        return `Payment_Detail_${this.lsqDatePipe
            .transform(paymentDate, 'shortDate')
            .replace(/\//g, '-')}`;
    }

    public getPaymentType(row: BuyerPaymentsSummaryItem): string {
        if (this.isPayLater) {
            if (row.paymentType === 'PAY_LATER_PAYMENT') {
                return this.buyerPaymentsPaymentTypeDisplayEnum.PAY_LATER;
            }
            if (row.paymentType === 'INVOICE_TERM' && row.payer === 'DEPOSIT_ACCOUNT') {
                return this.buyerPaymentsPaymentTypeDisplayEnum.PAY_LATER;
            }
            if (row.paymentType === 'EARLY_PAYMENT') {
                return this.buyerPaymentsPaymentTypeDisplayEnum.EARLY_PAYMENT;
            }
            if (row.paymentType === 'EARLY_PAY_REMITTANCE') {
                return this.buyerPaymentsPaymentTypeDisplayEnum.EARLY_PAY_REMITTANCE;
            }
            return Table.NullAmount;
        }

        return this.buyerPaymentsPaymentTypeDisplayEnum[
            row.paymentType as keyof typeof this.buyerPaymentsPaymentTypeDisplayEnum
        ];
    }
}
